import { Fragment, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import CreditItemDetails from 'components/credits/credit-details/CreditItemDetails';
import { CreditType } from 'types/credit';
import FullPageSpinner from 'components/common/FullPageSpinner';

import { Helmet } from 'react-helmet';
import { getHeadingAndSubHeading } from 'utils/required-credential';
import useArticulations from 'hooks/use-articulations';
import useFormSchema from 'hooks/use-form-schema';
import usePrecedents from 'hooks/use-precedents';
import useTranslation from 'hooks/use-translation';

export default function CreditDetails(): JSX.Element | null {
  const location = useLocation();
  const params = useParams();
  const translation = useTranslation();

  const formSchema = useFormSchema();
  const articulations = useArticulations();
  const precedents = usePrecedents();

  const articulation = useMemo(
    () => (params.creditItemID ? articulations.get(params.creditItemID) : undefined),
    [articulations, params.creditItemID],
  );
  const precedent = useMemo(
    () => (params.creditItemID ? precedents.get(params.creditItemID) : undefined),
    [precedents, params.creditItemID],
  );

  let component: JSX.Element | null = null;

  if (articulation && location.pathname.startsWith('/details/articulations/')) {
    component = <CreditItemDetails creditType={CreditType.ARTICULATION} articulation={articulation} />;
  }

  if (precedent && location.pathname.includes('/details/precedents/')) {
    component = <CreditItemDetails creditType={CreditType.PRECEDENT} precedent={precedent} />;
  }

  let title = '';

  if (!translation || !formSchema) {
    return <FullPageSpinner />;
  }

  if (articulation) {
    const { requiredCredentials } = articulation;
    const totalCredentials = requiredCredentials.length;
    if (totalCredentials > 0) {
      const credential = requiredCredentials[0];
      const { heading } = getHeadingAndSubHeading(credential, formSchema);
      if (totalCredentials === 1) {
        title = `Credit for ${heading}`;
      } else if (requiredCredentials.length > 1) {
        title = `Credit for ${heading} and ${totalCredentials - 1} others`;
      }
    }
  }
  if (precedent) {
    const { requiredCredentials } = precedent;
    const totalCredentials = requiredCredentials.length;
    if (totalCredentials > 0) {
      const credential = requiredCredentials[0];
      const { heading } = getHeadingAndSubHeading(credential, formSchema);
      if (totalCredentials === 1) {
        title = `Credit For ${heading}`;
      } else {
        title = `Credit For ${heading} and ${totalCredentials - 1} others`;
      }
    }
  }

  return (
    <Fragment>
      <Helmet>
        <title>{`${title} - ${translation.appName}`}</title>
      </Helmet>
      {component}
    </Fragment>
  );
}
